/**
 * 电商套图 · 分镜阶段：规划入口 + 分镜卡片网格 + 底部确认生成栏
 *
 * - 规划：AI 自动选题 / 手动勾选模板（弹窗内附补充要求输入），规划中展示流式原文预览；
 * - 分镜卡片：勾选、像素保护/创意徽标、状态徽标、点击进检查器编辑（标题/prompt/保护模式/删除）；
 * - 底部栏：已选分镜统计 + 全选 + 确认并生成。
 */
import { useState } from 'react';
import {
  Check,
  ChevronDown,
  Loader2,
  Pencil,
  Plus,
  Shield,
  Sparkles,
  Trash2,
  Wand2,
  X,
} from 'lucide-react';
import toast from '../../../components/Toast';
import { suiteTheme } from '../theme';
import type { SuiteShot, SuiteTemplateSummary } from '../api';

function StatusBadge({ status, errorMessage }: { status: SuiteShot['status']; errorMessage: string | null }) {
  if (status === 'success') return <span className={suiteTheme.badgeSuccess}>已生成</span>;
  if (status === 'generating') {
    return (
      <span className={suiteTheme.badgeMuted}>
        <Loader2 className="h-3 w-3 animate-spin" />
        生成中
      </span>
    );
  }
  if (status === 'failed') {
    return (
      <span className={suiteTheme.badgeDanger} title={errorMessage ?? undefined}>
        失败
      </span>
    );
  }
  return <span className={suiteTheme.badgeMuted}>待生成</span>;
}

function ShotCard({
  shot,
  selected,
  dragging,
  reordering,
  onDragStart,
  onDragEnd,
  onToggle,
  onEdit,
}: {
  shot: SuiteShot;
  selected: boolean;
  dragging: boolean;
  reordering: boolean;
  onDragStart: () => void;
  onDragEnd: () => void;
  onToggle: () => void;
  onEdit: () => void;
}) {
  const generable = shot.status === 'idle' || shot.status === 'failed';
  return (
    <div
      draggable={!reordering}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      className={`group relative h-full cursor-pointer overflow-hidden rounded-2xl border p-4 transition ${
        selected ? 'border-blue-400 bg-blue-50' : `${suiteTheme.card} hover:border-blue-300/70`
      } ${dragging ? 'opacity-40' : ''}`}
      onClick={onToggle}
      title="拖拽可调整顺序"
    >
      <span
        className={`flex h-5 w-5 items-center justify-center rounded-md border transition ${
          selected ? 'border-blue-200 bg-blue-400 text-zinc-950' : 'border-white/20 bg-transparent'
        }`}
      >
        {selected && <Check className="h-3.5 w-3.5" />}
      </span>

      <div className="mt-6 flex min-h-[64px] items-start">
        <h4 className="text-lg font-semibold leading-snug text-zinc-800">{shot.displayName}</h4>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span className={suiteTheme.badgeMuted}>1 张候选</span>
        {shot.protectionMode === 'PIXEL_PROTECTED' ? (
          <span className={suiteTheme.badgeGold}>
            <Shield className="h-3 w-3" />
            像素保护
          </span>
        ) : (
          <span className={suiteTheme.badgeMuted}>
            <Sparkles className="h-3 w-3" />
            创意
          </span>
        )}
        <StatusBadge status={shot.status} errorMessage={shot.errorMessage} />
      </div>

      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onEdit();
        }}
        className="absolute right-3 top-3 rounded-lg bg-zinc-800/80 p-1.5 text-zinc-600 opacity-0 transition group-hover:opacity-100 hover:text-blue-600"
        aria-label="编辑分镜"
      >
        <Pencil className="h-3.5 w-3.5" />
      </button>

      {generable && shot.errorMessage && (
        <p className="mt-2 line-clamp-1 text-xs text-rose-600/80">{shot.errorMessage}</p>
      )}
    </div>
  );
}

function TemplatePickerModal({
  templates,
  planning,
  onClose,
  onPlan,
}: {
  templates: SuiteTemplateSummary[];
  planning: boolean;
  onClose: () => void;
  onPlan: (payload: { mode: 'ai' | 'manual'; requestedTemplateIds: string[]; targetImageCount: number; userInstruction: string }) => void;
}) {
  const [mode, setMode] = useState<'ai' | 'manual'>('ai');
  const [picked, setPicked] = useState<string[]>([]);
  const [targetImageCount, setTargetImageCount] = useState(6);
  const [userInstruction, setUserInstruction] = useState('');

  const toggleTemplate = (id: string) => {
    setPicked((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <button aria-label="关闭" className="absolute inset-0 bg-zinc-950/70 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden ${suiteTheme.card}`}>
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <h3 className="text-base font-semibold text-zinc-800">规划套图分镜</h3>
          <button type="button" onClick={onClose} className="text-zinc-500 transition hover:text-zinc-700" aria-label="关闭">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setMode('ai')}
              className={`rounded-xl border p-4 text-left transition ${
                mode === 'ai' ? 'border-blue-400 bg-blue-100' : 'border-slate-200 bg-slate-100 hover:border-blue-300/70'
              }`}
            >
              <div className="flex items-center gap-2 text-sm font-semibold text-zinc-800">
                <Wand2 className="h-4 w-4 text-blue-600" />
                AI 自动选题
              </div>
              <p className="mt-1 text-xs leading-relaxed text-zinc-500">由 AI 根据商品信息与平台，从模板目录中规划一整套分镜。</p>
              <div className="mt-3 flex items-center gap-2">
                <span className="text-xs text-zinc-500">期望张数</span>
                <input
                  type="number"
                  min={1}
                  max={12}
                  value={targetImageCount}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => setTargetImageCount(Math.max(1, Math.min(12, Number(e.target.value) || 1)))}
                  className="w-16 rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 text-sm text-zinc-800 outline-none"
                />
              </div>
            </button>
            <button
              type="button"
              onClick={() => setMode('manual')}
              className={`rounded-xl border p-4 text-left transition ${
                mode === 'manual' ? 'border-blue-400 bg-blue-100' : 'border-slate-200 bg-slate-100 hover:border-blue-300/70'
              }`}
            >
              <div className="flex items-center gap-2 text-sm font-semibold text-zinc-800">
                <Plus className="h-4 w-4 text-blue-600" />
                手动勾选模板
              </div>
              <p className="mt-1 text-xs leading-relaxed text-zinc-500">从 25 种电商图模板中自行勾选，AI 按勾选顺序逐条规划。</p>
              <p className="mt-3 text-xs text-blue-600">已选 {picked.length} 个模板</p>
            </button>
          </div>

          {mode === 'manual' && (
            <div className="grid max-h-64 grid-cols-1 gap-2 overflow-y-auto sm:grid-cols-2">
              {templates.map((template) => {
                const checked = picked.includes(template.id);
                return (
                  <button
                    key={template.id}
                    type="button"
                    onClick={() => toggleTemplate(template.id)}
                    className={`flex items-start gap-2 rounded-xl border px-3 py-2.5 text-left transition ${
                      checked ? 'border-blue-400 bg-blue-100' : 'border-slate-200 bg-slate-100 hover:border-blue-300/70'
                    }`}
                  >
                    <span
                      className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                        checked ? 'border-blue-200 bg-blue-400 text-zinc-950' : 'border-white/20'
                      }`}
                    >
                      {checked && <Check className="h-3 w-3" />}
                    </span>
                    <span>
                      <span className="block text-sm text-zinc-800">{template.name}</span>
                      <span className="mt-0.5 block truncate text-xs text-zinc-500">{template.keywords.slice(0, 4).join(' / ')}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          <div>
            <label className={suiteTheme.label}>补充要求（可选）</label>
            <textarea
              className={`${suiteTheme.textarea} min-h-[64px]`}
              value={userInstruction}
              onChange={(e) => setUserInstruction(e.target.value)}
              placeholder="例如：突出清凉感，全部竖版构图，不要出现人物面部特写"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-slate-200 px-6 py-4">
          <button type="button" className={suiteTheme.ghostBtn} onClick={onClose} disabled={planning}>
            取消
          </button>
          <button
            type="button"
            className={suiteTheme.primaryBtn}
            disabled={planning || (mode === 'manual' && picked.length === 0)}
            onClick={() =>
              onPlan({
                mode,
                requestedTemplateIds: picked,
                targetImageCount,
                userInstruction: userInstruction.trim(),
              })
            }
          >
            {planning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            开始规划
          </button>
        </div>
      </div>
    </div>
  );
}

function ShotInspector({
  shot,
  saving,
  onClose,
  onUpdate,
  onDelete,
}: {
  shot: SuiteShot;
  saving: boolean;
  onClose: () => void;
  onUpdate: (shotId: string, patch: { displayName?: string; prompt?: string; protectionMode?: 'PIXEL_PROTECTED' | 'CREATIVE' }) => void;
  onDelete: (shotId: string) => void;
}) {
  const [displayName, setDisplayName] = useState(shot.displayName);
  const [prompt, setPrompt] = useState(shot.prompt ?? '');

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <button aria-label="关闭" className="absolute inset-0 bg-zinc-950/70 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative flex max-h-[85vh] w-full max-w-xl flex-col overflow-hidden ${suiteTheme.card}`}>
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <h3 className="text-base font-semibold text-zinc-800">编辑分镜</h3>
          <button type="button" onClick={onClose} className="text-zinc-500 transition hover:text-zinc-700" aria-label="关闭">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
          <div>
            <label className={suiteTheme.label}>场景标题</label>
            <input className={suiteTheme.input} value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
          </div>
          <div>
            <label className={suiteTheme.label}>生图提示词（规划产出，可自由改写）</label>
            <textarea
              className={`${suiteTheme.textarea} min-h-[160px]`}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
            />
          </div>
          <div>
            <label className={suiteTheme.label}>保护模式</label>
            <div className="flex gap-2">
              {(
                [
                  { key: 'PIXEL_PROTECTED', label: '像素保护', desc: '严格保持商品原貌' },
                  { key: 'CREATIVE', label: '创意', desc: '允许场景化演绎' },
                ] as const
              ).map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => onUpdate(shot.id, { protectionMode: item.key })}
                  className={`flex-1 rounded-xl border px-3 py-2.5 text-left transition ${
                    shot.protectionMode === item.key
                      ? 'border-blue-400 bg-blue-100'
                      : 'border-slate-200 bg-slate-100 hover:border-blue-300/70'
                  }`}
                >
                  <span className="block text-sm font-medium text-zinc-800">{item.label}</span>
                  <span className="mt-0.5 block text-xs text-zinc-500">{item.desc}</span>
                </button>
              ))}
            </div>
          </div>
          {shot.factClaims && (
            <p className="rounded-xl border border-slate-200 bg-slate-100 px-3 py-2 text-xs leading-relaxed text-zinc-500">
              依赖事实：{shot.factClaims}
            </p>
          )}
          {shot.riskFlags && (
            <p className="rounded-xl border border-blue-200/60 bg-blue-50 px-3 py-2 text-xs leading-relaxed text-blue-700">
              风险提示：{shot.riskFlags}
            </p>
          )}
        </div>
        <div className="flex items-center justify-between border-t border-slate-200 px-6 py-4">
          <button
            type="button"
            className={`inline-flex items-center gap-1.5 text-sm ${suiteTheme.dangerText}`}
            onClick={() => onDelete(shot.id)}
          >
            <Trash2 className="h-4 w-4" />
            删除分镜
          </button>
          <div className="flex gap-3">
            <button type="button" className={suiteTheme.ghostBtn} onClick={onClose}>
              取消
            </button>
            <button
              type="button"
              className={suiteTheme.primaryBtn}
              disabled={saving}
              onClick={() => {
                if (!displayName.trim()) {
                  toast.error('场景标题不能为空');
                  return;
                }
                onUpdate(shot.id, { displayName: displayName.trim(), prompt });
              }}
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              保存
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AddShotModal({
  templates,
  saving,
  onClose,
  onCreate,
}: {
  templates: SuiteTemplateSummary[];
  saving: boolean;
  onClose: () => void;
  onCreate: (payload: { templateId?: string; displayName: string; prompt?: string; protectionMode: 'PIXEL_PROTECTED' | 'CREATIVE' }) => void;
}) {
  const [displayName, setDisplayName] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [prompt, setPrompt] = useState('');
  const [protectionMode, setProtectionMode] = useState<'PIXEL_PROTECTED' | 'CREATIVE'>('PIXEL_PROTECTED');

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <button aria-label="关闭" className="absolute inset-0 bg-zinc-950/70 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative w-full max-w-lg overflow-hidden ${suiteTheme.card}`}>
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <h3 className="text-base font-semibold text-zinc-800">手动添加分镜</h3>
          <button type="button" onClick={onClose} className="text-zinc-500 transition hover:text-zinc-700" aria-label="关闭">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="space-y-4 px-6 py-5">
          <div>
            <label className={suiteTheme.label}>场景标题 *</label>
            <input
              className={suiteTheme.input}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="例如：桌面摆放场景图"
              autoFocus
            />
          </div>
          <div>
            <label className={suiteTheme.label}>关联模板（可选）</label>
            <select className={suiteTheme.select} value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
              <option value="">不关联</option>
              {templates.map((template) => (
                <option key={template.id} value={template.id}>{template.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={suiteTheme.label}>生图提示词（可选，留空则生成后自行编辑）</label>
            <textarea
              className={`${suiteTheme.textarea} min-h-[88px]`}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
            />
          </div>
          <div>
            <label className={suiteTheme.label}>保护模式</label>
            <select
              className={suiteTheme.select}
              value={protectionMode}
              onChange={(e) => setProtectionMode(e.target.value as 'PIXEL_PROTECTED' | 'CREATIVE')}
            >
              <option value="PIXEL_PROTECTED">像素保护</option>
              <option value="CREATIVE">创意</option>
            </select>
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 border-t border-slate-200 px-6 py-4">
          <button type="button" className={suiteTheme.ghostBtn} onClick={onClose} disabled={saving}>取消</button>
          <button
            type="button"
            className={suiteTheme.primaryBtn}
            disabled={saving || !displayName.trim()}
            onClick={() => onCreate({ templateId: templateId || undefined, displayName: displayName.trim(), prompt: prompt.trim() || undefined, protectionMode })}
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            添加
          </button>
        </div>
      </div>
    </div>
  );
}

export function StoryboardGrid({
  shots,
  templates,
  styleLock,
  selected,
  onToggleSelect,
  onSelectAll,
  onPlan,
  planning,
  planStreamText,
  onGenerateSelected,
  generating,
  onUpdateShot,
  onDeleteShot,
  savingShot,
  onCreateShot,
  creatingShot,
  onReorder,
  reordering,
}: {
  shots: SuiteShot[];
  templates: SuiteTemplateSummary[];
  styleLock: string | null;
  selected: string[];
  onToggleSelect: (shotId: string) => void;
  onSelectAll: () => void;
  onPlan: (payload: { mode: 'ai' | 'manual'; requestedTemplateIds: string[]; targetImageCount: number; userInstruction: string }) => void;
  planning: boolean;
  planStreamText: string;
  onGenerateSelected: (shotIds: string[]) => void;
  generating: boolean;
  onUpdateShot: (shotId: string, patch: { displayName?: string; prompt?: string; protectionMode?: 'PIXEL_PROTECTED' | 'CREATIVE' }) => void;
  onDeleteShot: (shotId: string) => void;
  savingShot: boolean;
  onCreateShot: (payload: { templateId?: string; displayName: string; prompt?: string; protectionMode: 'PIXEL_PROTECTED' | 'CREATIVE' }) => void;
  creatingShot: boolean;
  onReorder: (orderedIds: string[]) => void;
  reordering: boolean;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [inspecting, setInspecting] = useState<SuiteShot | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);

  const selectableIds = shots.filter((shot) => shot.status !== 'generating' && shot.status !== 'success').map((shot) => shot.id);
  const validSelection = selected.filter((id) => selectableIds.includes(id));

  const handleDrop = (targetId: string) => {
    const sourceId = draggingId;
    setDraggingId(null);
    if (!sourceId || sourceId === targetId) return;
    const ids = shots.map((shot) => shot.id);
    const from = ids.indexOf(sourceId);
    const to = ids.indexOf(targetId);
    if (from === -1 || to === -1) return;
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    onReorder(ids);
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" className={suiteTheme.primaryBtn} onClick={() => setPickerOpen(true)} disabled={planning}>
            <Sparkles className="h-4 w-4" />
            AI 规划分镜
          </button>
          <button
            type="button"
            className={suiteTheme.ghostBtn}
            onClick={() => onPlan({ mode: 'ai', requestedTemplateIds: [], targetImageCount: 6, userInstruction: '' })}
            disabled={planning}
          >
            <Wand2 className="h-4 w-4" />
            快速规划 6 张
          </button>
          <button type="button" className={suiteTheme.ghostBtn} onClick={() => setAddOpen(true)} disabled={planning || creatingShot}>
            <Plus className="h-4 w-4" />
            手动添加分镜
          </button>
          {styleLock && (
            <span className={`${suiteTheme.badgeGold} max-w-md truncate`} title={styleLock}>
              风格锁：{styleLock}
            </span>
          )}
        </div>
      </div>

      {planning && (
        <div className={`${suiteTheme.card} px-5 py-4`}>
          <div className="mb-2 flex items-center gap-2 text-sm text-blue-600">
            <Loader2 className="h-4 w-4 animate-spin" />
            AI 正在规划套图分镜…
          </div>
          {planStreamText && <div className={suiteTheme.streamPreview}>{planStreamText}</div>}
        </div>
      )}

      {shots.length === 0 && !planning ? (
        <div className={`${suiteTheme.card} flex flex-col items-center gap-3 px-6 py-16 text-center`}>
          <Sparkles className="h-8 w-8 text-zinc-600" />
          <p className="text-sm text-zinc-500">还没有分镜，点击「AI 规划分镜」生成一整套方案</p>
          <p className="max-w-md text-xs leading-relaxed text-zinc-600">
            规划会读取左侧的商品信息、已核实事实与禁止宣称，产出可编辑的分镜与生图提示词。
          </p>
        </div>
      ) : (
        shots.length > 0 && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {shots.map((shot) => (
            <div
              key={shot.id}
              onDragOver={(e) => {
                if (draggingId && draggingId !== shot.id) e.preventDefault();
              }}
              onDrop={() => handleDrop(shot.id)}
            >
              <ShotCard
                shot={shot}
                selected={selected.includes(shot.id)}
                dragging={draggingId === shot.id}
                reordering={reordering}
                onDragStart={() => setDraggingId(shot.id)}
                onDragEnd={() => setDraggingId(null)}
                onToggle={() => onToggleSelect(shot.id)}
                onEdit={() => setInspecting(shot)}
              />
            </div>
          ))}
        </div>
        )
      )}

      {shots.length > 0 && (
        <div className="sticky bottom-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white/95 px-5 py-3 backdrop-blur">
          <span className="text-sm text-zinc-600">
            <span className="font-semibold text-blue-600">{validSelection.length}</span> 个已选分镜
            <span className="ml-2 text-zinc-500">共 {shots.length} 个 · 预计 {validSelection.length} 张候选</span>
          </span>
          <div className="flex items-center gap-2">
            <button type="button" className={suiteTheme.ghostBtn} onClick={onSelectAll}>
              {selectableIds.length > 0 && selectableIds.every((id) => selected.includes(id)) ? '取消全选' : '全选'}
            </button>
            <button
              type="button"
              className={suiteTheme.primaryBtn}
              disabled={generating || validSelection.length === 0}
              onClick={() => onGenerateSelected(validSelection)}
            >
              {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
              确认并生成
            </button>
          </div>
        </div>
      )}

      {pickerOpen && (
        <TemplatePickerModal
          templates={templates}
          planning={planning}
          onClose={() => setPickerOpen(false)}
          onPlan={(payload) => {
            setPickerOpen(false);
            onPlan(payload);
          }}
        />
      )}

      {addOpen && (
        <AddShotModal
          templates={templates}
          saving={creatingShot}
          onClose={() => setAddOpen(false)}
          onCreate={(payload) => {
            setAddOpen(false);
            onCreateShot(payload);
          }}
        />
      )}

      {inspecting && (
        <ShotInspector
          shot={shots.find((shot) => shot.id === inspecting.id) ?? inspecting}
          saving={savingShot}
          onClose={() => setInspecting(null)}
          onUpdate={(shotId, patch) => {
            onUpdateShot(shotId, patch);
            if (!patch.protectionMode) setInspecting(null);
          }}
          onDelete={(shotId) => {
            onDeleteShot(shotId);
            setInspecting(null);
          }}
        />
      )}

      {/* 折叠提示：分镜数较多时用户可滚动查看 */}
      {shots.length > 9 && (
        <p className="flex items-center justify-center gap-1 pb-2 text-xs text-zinc-600">
          <ChevronDown className="h-3 w-3" />
          共 {shots.length} 个分镜
        </p>
      )}
    </div>
  );
}
